import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getListings, searchListings, searchUsers } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import ListingCard from '../components/ListingCard';
import SkeletonCard from '../components/SkeletonCard';

const STATUS_FILTERS = [
  { v: 'all', l: 'All' },
  { v: 'available', l: 'Available' },
  { v: 'deposit_paid', l: 'Deposit Paid' },
  { v: 'sold', l: 'Sold' },
];

const CATEGORY_ORDER = [
  'Clothing & Accessories',
  'Household',
  'Furniture',
  'Toys & Games',
  'Tools & Garden',
  'Electronics & Media',
  'Kitchenware',
  'Sports Equipment',
  'Miscellaneous',
];

const CATEGORY_ICONS = {
  'Clothing & Accessories': '👗',
  'Household':              '🏠',
  'Furniture':              '🛋️',
  'Toys & Games':           '🎮',
  'Tools & Garden':         '🔧',
  'Electronics & Media':    '📱',
  'Kitchenware':            '🍳',
  'Sports Equipment':       '⚽',
  'Miscellaneous':          '📦',
  'No Category':            '❓',
};

export default function Home() {
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [browseMode, setBrowseMode] = useState('items');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [activeSearch, setActiveSearch] = useState(searchParams.get('q') || '');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { load(activeSearch); }, [activeSearch]); // eslint-disable-line

  const load = async (query) => {
    setLoading(true);
    try {
      if (query) {
        const [listingResults, userResults] = await Promise.all([
          searchListings(query),
          searchUsers(query),
        ]);
        setListings(listingResults || []);
        setUsers(userResults || []);
      } else {
        setListings(await getListings() || []);
        setUsers([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    setActiveSearch(q);
    setFilter('all');
    setCategoryFilter('all');
    setBrowseMode('items');
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveSearch('');
    setSearchParams({});
    setFilter('all');
    setCategoryFilter('all');
  };

  const switchMode = (mode) => {
    setBrowseMode(mode);
    if (mode !== 'items') setCategoryFilter('all');
    setFilter('all');
  };

  const pickCategory = (cat) => {
    setCategoryFilter(cat);
    setBrowseMode('items');
    setFilter('all');
  };

  // Items visible after status + category filter
  const visible = listings.filter(l => {
    const statusOk = filter === 'all' || l.status === filter;
    const catOk = categoryFilter === 'all'
      || (categoryFilter === 'none' ? !l.category : l.category === categoryFilter);
    return statusOk && catOk;
  });

  // Sellers derived from listings
  const sellers = useMemo(() => {
    const map = {};
    listings.forEach(l => {
      const username = l.profiles?.username;
      if (!username) return;
      if (!map[username]) map[username] = { username, total: 0, available: 0 };
      map[username].total++;
      if (l.status === 'available') map[username].available++;
    });
    return Object.values(map).sort((a, b) => b.available - a.available || a.username.localeCompare(b.username));
  }, [listings]);

  // Category counts from all listings
  const categories = useMemo(() => {
    const counts = {};
    listings.forEach(l => {
      const cat = l.category || 'No Category';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    // Sort by defined order, then No Category last
    const result = CATEGORY_ORDER
      .filter(c => counts[c])
      .map(c => ({ name: c, count: counts[c] }));
    if (counts['No Category']) result.push({ name: 'No Category', count: counts['No Category'] });
    return result;
  }, [listings]);

  const activeCategoryLabel = categoryFilter === 'none' ? 'No Category'
    : categoryFilter !== 'all' ? categoryFilter : null;

  return (
    <>
      {/* HERO */}
      <div className="hero">
        <div className="hero-inner">
          <h1>Buy &amp; Sell <em>Easily</em></h1>
          <p>Browse, offer, buy and pay people directly, all for free.</p>

          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '.5rem', maxWidth: 480, margin: '0 auto 1rem', width: '100%' }}>
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search items or sellers…"
              style={{
                flex: 1, padding: '.75rem 1.1rem',
                borderRadius: 'var(--r)', border: 'none',
                fontSize: '1rem', background: 'rgba(255,255,255,.14)',
                color: 'var(--cream)', outline: 'none',
              }}
              onFocus={e => e.target.style.background = 'rgba(255,255,255,.22)'}
              onBlur={e => e.target.style.background = 'rgba(255,255,255,.14)'}
            />
            <button type="submit" className="btn btn-p" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              Search
            </button>
          </form>

          <div className="hero-btns">
            {user ? (
              <button className="btn btn-p" onClick={() => navigate('/sell')}>+ List an Item</button>
            ) : (
              <>
                <button className="btn btn-p" onClick={() => navigate('/register')}>Start Selling Free</button>
                <button className="btn btn-g" style={{ color: 'var(--cream)', borderColor: 'rgba(255,255,255,.3)' }} onClick={() => navigate('/login')}>Sign In</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="page">

        {/* Search result banner */}
        {activeSearch && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--cream)', borderRadius: 'var(--r)', padding: '.75rem 1rem', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '.5rem' }}>
            <span style={{ fontSize: '.9rem' }}>
              Results for <strong>"{activeSearch}"</strong>
              <span style={{ color: 'var(--brown)', marginLeft: '.5rem' }}>
                — {listings.length} {listings.length === 1 ? 'item' : 'items'}{users.length > 0 ? `, ${users.length} ${users.length === 1 ? 'seller' : 'sellers'}` : ''}
              </span>
            </span>
            <button className="btn btn-g btn-sm" onClick={clearSearch}>✕ Clear</button>
          </div>
        )}

        {/* Seller results (search only) */}
        {activeSearch && users.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <div className="section-title" style={{ marginBottom: '.75rem' }}>
              Sellers matching "{activeSearch}"
            </div>
            <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
              {users.map(u => (
                <div
                  key={u.id}
                  onClick={() => navigate(`/seller/${u.username}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '.75rem', background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '.75rem 1.1rem', cursor: 'pointer', transition: 'all .18s', minWidth: 180 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--terracotta)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--terracotta)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontFamily: 'var(--font-d)', fontSize: '1.1rem', flexShrink: 0 }}>
                    {u.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--ink)' }}>{u.username}</div>
                    <div style={{ fontSize: '.75rem', color: 'var(--brown)' }}>View listings →</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Browse mode toggle — Items / Sellers / Categories */}
        {!activeSearch && (
          <div className="browse-toggle">
            <button className={`chip ${browseMode === 'items' ? 'on' : ''}`} onClick={() => switchMode('items')}>
              Items {!loading && `(${listings.length})`}
            </button>
            <button className={`chip ${browseMode === 'sellers' ? 'on' : ''}`} onClick={() => switchMode('sellers')}>
              Sellers {!loading && sellers.length > 0 ? `(${sellers.length})` : ''}
            </button>
            <button className={`chip ${browseMode === 'categories' ? 'on' : ''}`} onClick={() => switchMode('categories')}>
              Categories {!loading && categories.length > 0 ? `(${categories.length})` : ''}
            </button>
          </div>
        )}

        {/* Page header */}
        {!activeSearch && (
          <div className="page-header">
            <h1 className="page-title">
              {browseMode === 'sellers' ? 'Browse Sellers'
                : browseMode === 'categories' ? 'Browse by Category'
                : activeCategoryLabel ? activeCategoryLabel
                : 'Browse Listings'}
            </h1>
            <p className="page-sub">
              {browseMode === 'sellers'
                ? `${sellers.length} active seller${sellers.length !== 1 ? 's' : ''}`
                : browseMode === 'categories'
                ? `${categories.length} categor${categories.length !== 1 ? 'ies' : 'y'}`
                : activeCategoryLabel
                ? `${visible.length} item${visible.length !== 1 ? 's' : ''} in this category`
                : `${listings.length} item${listings.length !== 1 ? 's' : ''} for sale`}
            </p>
          </div>
        )}

        {/* Matching items label (search only) */}
        {activeSearch && listings.length > 0 && (
          <div className="section-title" style={{ marginBottom: '.75rem' }}>
            Items matching "{activeSearch}"
          </div>
        )}

        {/* Active category indicator — shown in items mode when a category is selected */}
        {browseMode === 'items' && activeCategoryLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '.82rem', color: 'var(--brown)' }}>Category:</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', background: 'var(--terracotta)', color: '#fff', borderRadius: 20, padding: '.2rem .75rem', fontSize: '.82rem', fontWeight: 600 }}>
              {CATEGORY_ICONS[activeCategoryLabel]} {activeCategoryLabel}
              <button
                onClick={() => setCategoryFilter('all')}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, fontSize: '.9rem', lineHeight: 1, marginLeft: '.1rem', opacity: .8 }}
              >✕</button>
            </span>
          </div>
        )}

        {/* Status filters — items mode only */}
        {(browseMode === 'items' || activeSearch) && (
          <div className="filter-bar">
            {STATUS_FILTERS.map(f => (
              <button key={f.v} className={`chip ${filter === f.v ? 'on' : ''}`} onClick={() => setFilter(f.v)}>{f.l}</button>
            ))}
          </div>
        )}

        {/* Items grid */}
        {(browseMode === 'items' || activeSearch) && (
          loading ? (
            <div className="grid">
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : visible.length === 0 && (!activeSearch || users.length === 0) ? (
            <div className="empty">
              <div className="empty-icon">{activeSearch ? '🔍' : activeCategoryLabel ? '🏷️' : '📦'}</div>
              <h3>
                {activeSearch ? `No items found for "${activeSearch}"`
                  : activeCategoryLabel ? `No items in ${activeCategoryLabel}`
                  : 'No listings yet'}
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                {activeSearch ? 'Try different keywords.'
                  : activeCategoryLabel ? 'Try another category.'
                  : 'Be the first to post something!'}
              </p>
              {activeSearch && <button className="btn btn-g" onClick={clearSearch}>Browse all listings</button>}
              {activeCategoryLabel && <button className="btn btn-g" onClick={() => setCategoryFilter('all')}>Clear category filter</button>}
            </div>
          ) : visible.length > 0 ? (
            <div className="grid">
              {visible.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>
          ) : null
        )}

        {/* Sellers grid */}
        {!activeSearch && browseMode === 'sellers' && (
          loading ? (
            <div className="sellers-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="seller-card" style={{ pointerEvents: 'none', gap: '.75rem' }}>
                  <div className="skeleton" style={{ width: 52, height: 52, borderRadius: '50%' }} />
                  <div className="skeleton" style={{ height: 14, width: '65%', borderRadius: 6 }} />
                  <div className="skeleton" style={{ height: 11, width: '45%', borderRadius: 6 }} />
                </div>
              ))}
            </div>
          ) : sellers.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">👥</div>
              <h3>No sellers yet</h3>
              <p>Be the first to list something!</p>
            </div>
          ) : (
            <div className="sellers-grid">
              {sellers.map(s => (
                <div key={s.username} className="seller-card" onClick={() => navigate(`/seller/${s.username}`)}>
                  <div className="seller-avatar">{s.username.charAt(0).toUpperCase()}</div>
                  <div className="seller-name">{s.username}</div>
                  <div className="seller-meta">
                    {s.available > 0
                      ? `${s.available} available${s.total !== s.available ? ` · ${s.total} total` : ''}`
                      : `${s.total} listing${s.total !== 1 ? 's' : ''}`}
                  </div>
                  <div className="seller-cta">Browse shop →</div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Categories grid */}
        {!activeSearch && browseMode === 'categories' && (
          loading ? (
            <div className="sellers-grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="seller-card" style={{ pointerEvents: 'none' }}>
                  <div className="skeleton" style={{ width: 44, height: 44, borderRadius: '50%' }} />
                  <div className="skeleton" style={{ height: 14, width: '70%', borderRadius: 6 }} />
                  <div className="skeleton" style={{ height: 11, width: '40%', borderRadius: 6 }} />
                </div>
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">🏷️</div>
              <h3>No categories yet</h3>
              <p>Items will appear here once sellers add categories to their listings.</p>
            </div>
          ) : (
            <div className="sellers-grid">
              {categories.map(c => (
                <div
                  key={c.name}
                  className="seller-card"
                  onClick={() => pickCategory(c.name === 'No Category' ? 'none' : c.name)}
                >
                  <div className="seller-avatar" style={{ fontSize: '1.4rem', background: c.name === 'No Category' ? '#aaa' : 'var(--terracotta)' }}>
                    {CATEGORY_ICONS[c.name] || '📦'}
                  </div>
                  <div className="seller-name">{c.name}</div>
                  <div className="seller-meta">{c.count} {c.count === 1 ? 'item' : 'items'}</div>
                  <div className="seller-cta">Browse →</div>
                </div>
              ))}
            </div>
          )
        )}

      </div>
    </>
  );
}
